/**
 * MapPickerModal — full-screen sheet with a Leaflet map. User drags the pin
 * (or taps anywhere on the map) to choose a location. On confirm, we run a
 * reverse geocode against Nominatim and hand back { latitude, longitude,
 * address } to the parent.
 *
 * Props:
 *   initial   — { latitude, longitude } starting position (falls back to
 *                Kerala centroid if missing)
 *   nearby    — optional array of { name, lat, lng } markers to render as
 *                "suggested" pins (e.g. nearby hospitals for ambulance drops)
 *   onCancel()          — close without saving
 *   onConfirm(patch)    — receive { latitude, longitude, address }
 *   title               — modal title (default "Pick location on map")
 */
import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import { X, Check, MapPin, ArrowClockwise, Hospital, MagnifyingGlass } from "@phosphor-icons/react";

// Fix Leaflet's default marker icons (webpack-bundling gotcha).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});
const suggestionIcon = new L.DivIcon({
  className: "",
  html: `<div style="background:#2563EB;color:white;border-radius:50%;width:26px;height:26px;
                   display:flex;align-items:center;justify-content:center;box-shadow:0 3px 8px rgba(0,0,0,0.25);
                   border:2px solid white;font-size:14px;font-weight:bold;">H</div>`,
  iconSize: [26, 26], iconAnchor: [13, 13],
});

const FALLBACK_CENTER = { latitude: 8.5241, longitude: 76.9366 };  // Trivandrum

export default function MapPickerModal({
  initial, nearby = [], onCancel, onConfirm, title = "Pick location on map",
}) {
  const start = {
    latitude: initial?.latitude ?? FALLBACK_CENTER.latitude,
    longitude: initial?.longitude ?? FALLBACK_CENTER.longitude,
  };
  const [pos, setPos] = useState(start);
  const [addr, setAddr] = useState("");
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  const reverseGeocode = async (lat, lng) => {
    setBusy(true);
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        { headers: { Accept: "application/json" } },
      );
      const j = await r.json();
      setAddr(j?.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`);
    } catch {
      setAddr(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      reverseGeocode(pos.latitude, pos.longitude);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [pos.latitude, pos.longitude]);

  const confirm = () => {
    if (!addr) { toast.error("Waiting for address…"); return; }
    onConfirm({
      latitude: pos.latitude,
      longitude: pos.longitude,
      address: addr,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
         onClick={onCancel} data-testid="map-picker-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-lg bg-white rounded-t-3xl sm:rounded-2xl overflow-hidden flex flex-col"
           style={{ height: "min(90vh, 720px)" }}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="font-display font-black text-[15px] text-[#111827] inline-flex items-center gap-1.5">
            <MapPin size={15} weight="fill" className="text-[#DC2626]"/>{title}
          </div>
          <button onClick={onCancel} data-testid="map-picker-close"
                  className="w-8 h-8 rounded-full bg-gray-100 inline-flex items-center justify-center">
            <X size={14} weight="bold"/>
          </button>
        </div>

        {/* Type-to-search location bar (Nominatim) */}
        <MapSearchBar onPick={(p) => setPos(p)}/>

        <div className="flex-1 relative">
          <MapContainer center={[pos.latitude, pos.longitude]} zoom={14}
                        className="w-full h-full" scrollWheelZoom={true}>
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
            />
            <PanTo pos={pos}/>
            <DraggablePin pos={pos} onChange={setPos}/>
            {nearby.map((n, i) => (
              <Marker key={i} position={[n.lat, n.lng]} icon={suggestionIcon}
                      eventHandlers={{ click: () => setPos({ latitude: n.lat, longitude: n.lng }) }}/>
            ))}
          </MapContainer>
        </div>

        <div className="px-4 py-3 border-t border-gray-100 bg-[#F7F6F2]">
          <div className="text-[10px] uppercase tracking-widest text-[#5C5A55] font-bold">Selected location</div>
          <div className="mt-1 flex items-start gap-1.5 text-[12px] text-[#1A1A1A]"
                data-testid="map-picker-address">
            {busy ? <ArrowClockwise size={13} className="animate-spin mt-0.5 shrink-0"/>
                  : <MapPin size={13} weight="fill" className="mt-0.5 shrink-0 text-[#DC2626]"/>}
            <span className="line-clamp-2 break-words">{addr || "Tap the map to choose…"}</span>
          </div>
          <div className="mt-1 font-mono text-[10px] text-[#8A8780]">
            {pos.latitude.toFixed(5)}, {pos.longitude.toFixed(5)}
          </div>
          {nearby.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest text-[#5C5A55] font-bold inline-flex items-center gap-1">
                <Hospital size={11} weight="fill" className="text-[#2563EB]"/> Nearby hospitals
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {nearby.map((n, i) => (
                  <button key={i} type="button"
                          onClick={() => setPos({ latitude: n.lat, longitude: n.lng })}
                          data-testid={`map-picker-nearby-${i}`}
                          className="h-8 px-2.5 text-[11px] font-semibold bg-white border border-[#DBEAFE] text-[#1D4ED8] rounded-full hover:bg-[#EFF6FF]">
                    {n.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="p-3 grid grid-cols-2 gap-2">
          <button onClick={onCancel}
                  className="h-11 rounded-md border border-[#E5E2D9] text-[13px] font-semibold">
            Cancel
          </button>
          <button onClick={confirm} disabled={!addr}
                  data-testid="map-picker-confirm"
                  className="h-11 rounded-md bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white text-[13px] font-bold inline-flex items-center justify-center gap-1.5">
            <Check size={14} weight="bold"/> Use this location
          </button>
        </div>
      </div>
    </div>
  );
}

function DraggablePin({ pos, onChange }) {
  useMapEvents({
    click(e) { onChange({ latitude: e.latlng.lat, longitude: e.latlng.lng }); },
  });
  return (
    <Marker
      position={[pos.latitude, pos.longitude]}
      draggable={true}
      eventHandlers={{
        dragend(e) {
          const ll = e.target.getLatLng();
          onChange({ latitude: ll.lat, longitude: ll.lng });
        },
      }}
    />
  );
}

// Recenter the Leaflet map whenever `pos` changes (e.g. from a search hit).
function PanTo({ pos }) {
  const map = useMap();
  useEffect(() => {
    if (pos?.latitude != null && pos?.longitude != null) {
      map.setView([pos.latitude, pos.longitude], Math.max(map.getZoom(), 15));
    }
    // eslint-disable-next-line
  }, [pos.latitude, pos.longitude]);
  return null;
}

// Nominatim-backed search bar rendered above the map.
function MapSearchBar({ onPick }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const t = useRef(null);

  useEffect(() => {
    if (t.current) clearTimeout(t.current);
    if (q.trim().length < 3) { setHits([]); return; }
    t.current = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&countrycodes=in&q=${encodeURIComponent(q)}`,
          { headers: { Accept: "application/json" } },
        );
        const j = await r.json();
        setHits(Array.isArray(j) ? j : []);
        setOpen(true);
      } catch { setHits([]); }
      finally { setBusy(false); }
    }, 400);
    return () => clearTimeout(t.current);
  }, [q]);

  return (
    <div className="relative px-3 py-2 border-b border-gray-100 bg-white">
      <div className="flex items-center gap-2 h-10 px-3 border border-slate-200 rounded-md">
        <MagnifyingGlass size={13} weight="duotone" className="text-slate-400"/>
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                placeholder="Search a location…"
                data-testid="map-picker-search"
                className="flex-1 text-sm outline-none"/>
        {busy && <ArrowClockwise size={12} className="animate-spin text-slate-400"/>}
      </div>
      {open && hits.length > 0 && (
        <div className="absolute left-3 right-3 mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-20 max-h-64 overflow-auto"
             data-testid="map-picker-search-results">
          {hits.map((h, i) => (
            <button key={i} type="button"
                     onClick={() => {
                       onPick({ latitude: parseFloat(h.lat), longitude: parseFloat(h.lon) });
                       setOpen(false);
                       setQ(h.display_name.split(",").slice(0, 2).join(","));
                     }}
                     data-testid={`map-picker-hit-${i}`}
                     className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
              <div className="text-[12px] font-semibold text-slate-900 truncate">
                {h.display_name.split(",").slice(0, 2).join(",")}
              </div>
              <div className="text-[10px] text-slate-500 truncate">{h.display_name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
